import { useCallback, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * Drag-to-reorder wrapper for the link list / grid.
 *
 * How it ties into the rest of RefClick
 * -------------------------------------
 * `sort_order` on each link doc drives:
 *   * the daily-random-target distribution (slots #1-#2 roll 2-4,
 *     everything else 1-4),
 *   * numbering in the ↗ open-in-new-tab shortcut hint (1..9),
 *   * the order shown here.
 *
 * CRITICAL invariant: today's effective targets must NOT re-roll on
 * reorder. The backend guarantees that by only writing to `sort_order`
 * on the link doc and leaving `daily_targets[(link, today)]` untouched;
 * see `routers/links.py::reorder_links` for the proof.
 *
 * UX decisions
 * ------------
 *   * Drag handle (a GripVertical icon) rather than whole-card drag so
 *     the +1 / −1 / open-in-new-tab buttons AND card-click-to-expand
 *     all keep working without any gymnastics.
 *   * MouseSensor + TouchSensor with a 220 ms press delay — needed
 *     because the card is already tappable (click = open detail); the
 *     delay disambiguates tap from drag on mobile.
 *   * KeyboardSensor for a11y: tab onto a handle, Space to lift,
 *     arrow keys to move, Space to drop. @dnd-kit does the ARIA
 *     announcements out of the box.
 *   * Grid uses `rectSortingStrategy` (2D), list uses
 *     `verticalListSortingStrategy` (1D) — both are built in.
 *   * We OPTIMISTICALLY swap state on drop, then call the server. If
 *     the server rejects we revert via the parent (`onReorder` returns
 *     a promise; thrown error → caller rolls back).
 */
export default function SortableLinks({
  items,                    // array of link docs (must have `id`)
  variant,                  // "row" | "card"
  onReorder,                // async (newIds: string[]) => void
  renderItem,               // (link, idx, dragHandleProps) => ReactNode
}) {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    // 220 ms press-and-hold prevents accidental drags when the owner
    // just means to tap the card to open details.
    useSensor(MouseSensor,   { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 220, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = useMemo(() => items.map((l) => l.id), [items]);

  const handleDragEnd = useCallback(
    async (event) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = ids.indexOf(active.id);
      const newIdx = ids.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return;
      const nextIds = arrayMove(ids, oldIdx, newIdx);
      try {
        await onReorder(nextIds);
      } catch {
        /* parent surfaces the error toast + reverts optimistically */
      }
    },
    [ids, onReorder],
  );

  const strategy =
    variant === "card" ? rectSortingStrategy : verticalListSortingStrategy;

  const activeItem = activeId ? items.find((i) => i.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={ids} strategy={strategy}>
        {items.map((link, idx) => (
          <SortableLinkRow
            key={link.id}
            id={link.id}
            renderItem={(dragHandleProps) =>
              renderItem(link, idx, dragHandleProps)
            }
          />
        ))}
      </SortableContext>
      {/* Floating ghost of the dragged item — matches the row/card look. */}
      <DragOverlay dropAnimation={{ duration: 220, easing: "cubic-bezier(0.16, 1, 0.3, 1)" }}>
        {activeItem ? (
          <div
            className="pointer-events-none scale-[1.02] opacity-95"
            style={{ filter: "drop-shadow(0 10px 24px rgb(var(--accent) / 0.35))" }}
          >
            {renderItem(activeItem, items.indexOf(activeItem), null)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * Internal: one row/card that exposes `dragHandleProps` so the caller
 * can mount a visible grip icon exactly where it belongs in the card.
 */
function SortableLinkRow({ id, renderItem }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  // While dragging we HIDE the real row (DragOverlay renders the ghost)
  // so there's no gap-flicker during the DOM shuffle.
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
    willChange: "transform",
  };

  // Package the drag listeners + a nice grip icon so the caller just
  // slots this onto their card. a11y lives on the handle BUTTON.
  const dragHandle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      className="group inline-flex h-7 w-5 shrink-0 cursor-grab items-center justify-center rounded text-text-muted/70 opacity-60 transition-opacity hover:text-accent focus:opacity-100 focus:outline-none active:cursor-grabbing"
      aria-label="Drag to reorder"
      data-testid={`drag-handle-${id}`}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );

  return (
    <div ref={setNodeRef} style={style}>
      {renderItem(dragHandle)}
    </div>
  );
}
